import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';
import { Spin } from './Spin';
import styles from './Card.module.css';

// ── CVA ───────────────────────────────────────────────────────────────────────

const card = cva(styles.base, {
  variants: {
    bordered: { true: styles.bordered },
    hoverable: { true: styles.hoverable },
  },
  defaultVariants: {
    bordered: true,
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface CardMetaProps {
  avatar?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

function CardMeta({ avatar, title, description, style, className }: CardMetaProps) {
  return (
    <div className={clsx(styles.meta, className)} style={style}>
      {avatar && <div className={styles.metaAvatar}>{avatar}</div>}
      <div className={styles.metaContent}>
        {title && <div className={styles.metaTitle}>{title}</div>}
        {description && <div className={styles.metaDescription}>{description}</div>}
      </div>
    </div>
  );
}

type DivMotionSafeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface CardProps extends Omit<DivMotionSafeProps, 'title'> {
  title?: React.ReactNode;
  extra?: React.ReactNode;
  cover?: React.ReactNode;
  actions?: React.ReactNode[];
  bordered?: boolean;
  hoverable?: boolean;
  size?: 'default' | 'small';
  loading?: boolean;
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

/**
 * Card — content container with optional header, cover, and actions.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | title | ReactNode | — | Header title |
 * | extra | ReactNode | — | Header right slot |
 * | cover | ReactNode | — | Cover image/element |
 * | actions | ReactNode[] | — | Footer action buttons |
 * | bordered | boolean | true | Show border |
 * | hoverable | boolean | false | Hover lift effect |
 * | size | 'default' \| 'small' | 'default' | Padding size |
 * | loading | boolean | false | Show skeleton |
 * | bodyStyle | CSSProperties | — | Body style override |
 *
 * Sub: Card.Meta
 *
 * @example
 * <Card title="Note" extra={<a>More</a>}>Content</Card>
 * <Card hoverable cover={<img src="..." />}>
 *   <Card.Meta title="Title" description="Description" />
 * </Card>
 */
export function Card({
  title,
  extra,
  cover,
  actions,
  bordered = true,
  hoverable = false,
  size = 'default',
  loading = false,
  bodyStyle,
  children,
  style,
  className,
  ...props
}: CardProps) {
  const safeProps = props;
  const shouldReduce = useReducedMotion();
  // Padding is a size-driven value — CSS custom property
  const pad = size === 'small' ? 'var(--space-4)' : 'var(--space-6)';

  return (
    <motion.div
      whileHover={hoverable && !shouldReduce ? { y: -2 } : undefined}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className={card({ bordered, hoverable, className })}
      style={style}
      {...safeProps}
    >
      {cover && <div>{cover}</div>}

      {(title || extra) && (
        <div
          className={clsx(
            styles.header,
            size === 'small' ? styles.headerSmall : styles.headerDefault,
          )}
        >
          {title && <div className={styles.title}>{title}</div>}
          {extra && <div className={styles.extra}>{extra}</div>}
        </div>
      )}

      <div
        className={styles.body}
        style={{ '--card-pad': pad, ...bodyStyle } as React.CSSProperties}
      >
        {loading ? <Spin tip="Loading..." /> : children}
      </div>

      {actions && actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map((action, i) => (
            <div key={i} className={styles.actionItem}>
              {action}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

Card.Meta = CardMeta;
